import React from "react";
import { Link } from "react-router-dom";
import { HelpCircle, X } from "lucide-react";

export interface FundingCalculatorHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FundingCalculatorHelpModal: React.FC<
  FundingCalculatorHelpModalProps
> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />

        <div className="relative bg-surface rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b bg-gradient-to-r from-brand/10 to-focus/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand/15 rounded-lg">
                  <HelpCircle className="w-5 h-5 text-brand" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">
                    Funding Calculator - How It Works
                  </h2>
                  <p className="text-sm text-white/65">
                    Understanding the equations and methodology
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/[0.06] rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white/45" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] space-y-6">
            {/* Core Calculations */}
            <div className="bg-surface-elevated p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-3">
                📊 Core Financial Calculations
              </h3>

              <div className="space-y-4">
                <div className="bg-surface p-4 rounded border-l-4 border-blue-500">
                  <h4 className="font-medium text-white mb-2">
                    Monthly Burn Rate
                  </h4>
                  <div className="font-mono text-sm bg-white/[0.06] p-2 rounded text-white">
                    Net Monthly Burn = Monthly Expenses - Monthly Revenue
                  </div>
                  <p className="text-sm text-white/75 mt-2">
                    This is your core cash consumption rate, accounting for all
                    recurring income and expenses.
                  </p>
                </div>

                <div className="bg-surface p-4 rounded border-l-4 border-green-500">
                  <h4 className="font-medium text-white mb-2">
                    Current Runway
                  </h4>
                  <div className="font-mono text-sm bg-white/[0.06] p-2 rounded text-white">
                    Runway (months) = Current Cash ÷ Net Monthly Burn
                  </div>
                  <p className="text-sm text-white/75 mt-2">
                    How long your current cash will last at the current burn
                    rate. If profitable (negative burn), runway = ∞
                  </p>
                </div>

                <div className="bg-surface p-4 rounded border-l-4 border-purple-500">
                  <h4 className="font-medium text-white mb-2">
                    Growth Factor
                  </h4>
                  <div className="font-mono text-sm bg-white/[0.06] p-2 rounded text-white">
                    Growth Factor = (1 + growth_rate)^months_elapsed
                  </div>
                  <p className="text-sm text-white/75 mt-2">
                    Expenses and revenues can grow over time using compound
                    growth formulas.
                  </p>
                </div>
              </div>
            </div>

            {/* Funding perspectives */}
            <div className="bg-surface-elevated p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-3">
                🔄 Two Calculation Modes
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-surface p-4 rounded border-l-4 border-brand">
                  <h4 className="font-medium text-emerald-100 mb-2">
                    Initial raise (day 0)
                  </h4>
                  <div className="font-mono text-xs bg-brand/10 p-2 rounded mb-2 text-white">
                    Ask = Net Burn × Months
                  </div>
                  <p className="text-xs text-white/75">
                    Sizes your opening seed ask assuming $0 in the bank at
                    launch. Uses burn and revenue at your startup start date.
                  </p>
                </div>
                <div className="bg-surface p-4 rounded border-l-4 border-blue-500">
                  <h4 className="font-medium text-white mb-2">
                    From timeline date
                  </h4>
                  <div className="font-mono text-xs bg-white/[0.06] p-2 rounded mb-2 text-white">
                    Ask = max((Burn × Months) - Current Cash, 0)
                  </div>
                  <p className="text-xs text-white/75">
                    Additional capital needed from where you are on the
                    timeline — subtracts available cash from each scenario.
                  </p>
                </div>
              </div>
            </div>

            {/* Funding Scenarios */}
            <div className="bg-warn/10 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-3">
                🎯 Funding Scenarios
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-surface p-4 rounded border">
                  <h4 className="font-medium text-rose-100 mb-2">
                    🚨 Survival Mode (6 months)
                  </h4>
                  <div className="font-mono text-xs bg-danger/10 p-2 rounded mb-2 text-white">
                    Initial: Burn × 6 · Timeline: max((Burn × 6) - Cash, 0)
                  </div>
                  <p className="text-xs text-white/75">
                    Minimum viable runway for basic operations until next
                    milestone.
                  </p>
                </div>

                <div className="bg-surface p-4 rounded border">
                  <h4 className="font-medium text-warn mb-2">
                    📈 Growth Mode (15 months)
                  </h4>
                  <div className="font-mono text-xs bg-warn/10 p-2 rounded mb-2 text-white">
                    Initial: Burn × 15 · Timeline: max((Burn × 15) - Cash, 0)
                  </div>
                  <p className="text-xs text-white/75">
                    Runway for product development and market expansion.
                  </p>
                </div>

                <div className="bg-surface p-4 rounded border">
                  <h4 className="font-medium text-emerald-100 mb-2">
                    🚀 Series Prep (24 months)
                  </h4>
                  <div className="font-mono text-xs bg-brand/10 p-2 rounded mb-2 text-white">
                    Initial: Burn × 24 · Timeline: max((Burn × 24) - Cash, 0)
                  </div>
                  <p className="text-xs text-white/75">
                    Time to hit key metrics for next funding round.
                  </p>
                </div>
              </div>
            </div>

            {/* Special Features */}
            <div className="bg-brand/10 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-3">
                ⚡ Advanced Features
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-surface p-3 rounded border">
                  <h4 className="font-medium text-white mb-1">
                    🔧 Expense Types
                  </h4>
                  <ul className="text-sm text-white/75 space-y-1">
                    <li>
                      • <strong>Fixed:</strong> Static monthly amount with
                      optional growth
                    </li>
                    <li>
                      • <strong>Percentage:</strong> Scales with revenue (e.g.,
                      3% payment processing)
                    </li>
                    <li>
                      • <strong>Hybrid:</strong> Fixed base + revenue percentage
                    </li>
                  </ul>
                </div>

                <div className="bg-surface p-3 rounded border">
                  <h4 className="font-medium text-white mb-1">
                    📅 Timing Logic
                  </h4>
                  <ul className="text-sm text-white/75 space-y-1">
                    <li>
                      • <strong>URGENT:</strong> ≤6 months runway
                    </li>
                    <li>
                      • <strong>Soon:</strong> ≤12 months runway
                    </li>
                    <li>
                      • <strong>Plan ahead:</strong> &gt;12 months runway
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Profitable Company Handling */}
            <div className="bg-indigo-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-3">
                💰 Profitable Company Logic
              </h3>
              <div className="bg-surface p-4 rounded border">
                <p className="text-sm text-white/75 mb-2">
                  For companies with positive cash flow (Revenue &gt; Expenses),
                  the calculator adjusts recommendations:
                </p>
                <ul className="text-sm text-white/75 space-y-1 ml-4">
                  <li>
                    • <strong>Survival Mode:</strong> 50% multiplier for
                    conservative growth buffer
                  </li>
                  <li>
                    • <strong>Growth Mode:</strong> 75% multiplier for
                    accelerated expansion
                  </li>
                  <li>
                    • <strong>Series Prep:</strong> Full amount for aggressive
                    market capture
                  </li>
                </ul>
              </div>
            </div>

            {/* Key Insights */}
            <div className="bg-brand/10 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-3">
                💡 Key Insights
              </h3>
              <div className="space-y-2 text-sm text-white/75">
                <p>
                  • <strong>Conservative by design:</strong> Accounts for growth
                  scenarios and unexpected costs
                </p>
                <p>
                  • <strong>Dynamic modeling:</strong> Uses actual growth rates
                  rather than static assumptions
                </p>
                <p>
                  • <strong>Revenue-aware:</strong> Some expenses automatically
                  scale with business growth
                </p>
                <p>
                  • <strong>Time-sensitive:</strong> Provides actionable timing
                  recommendations
                </p>
                <p>
                  • <strong>Context-aware:</strong> Different logic for burning
                  vs. profitable companies
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 px-6 py-4">
            <Link
              to="/help/funding-calculator"
              className="text-sm font-semibold text-brand hover:text-emerald-200"
              onClick={onClose}
            >
              View full funding calculator reference in Help →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
